'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { memo } from 'react'
import { useForm, useFormContext, useWatch } from 'react-hook-form'

import { useUploadUrl } from '@/entities/storage'
import { ImageUpload } from '@/shared/lib'
import { ContentBox, Icon, Notice } from '@/shared/ui'
import { Button, Dialog, Form, Image } from '@/shared/ui'

import { useValidatePhoneNumber } from '../api/validate-phone-number'
import PassportSampleImage from '../asset/passport-sample.png'
import {
  passportVerificationFormDefaultValues,
  TPassportVerificationForm,
  createZPassportVerificationForm,
} from '../model/passport-verification-form'

export const PASSPORT_VERIFICATION_DIALOG = 'PASSPORT_VERIFICATION'

type Props = {
  isOpen: boolean
  onClose: () => void
  onSuccess: (formValues: TPassportVerificationForm) => void
  defaultValues?: Partial<TPassportVerificationForm>
}

export const PassportDialog = ({
  isOpen,
  onClose,
  onSuccess,
  defaultValues,
}: Props) => {
  const t = useTranslations('passport_verification')

  const { validatePhoneNumberAsync } = useValidatePhoneNumber()

  const form = useForm<TPassportVerificationForm>({
    defaultValues: defaultValues || passportVerificationFormDefaultValues,
    resolver: zodResolver(createZPassportVerificationForm(t)),
    reValidateMode: 'onSubmit',
  })

  const { handleSubmit } = form

  const submitForm = async ({
    contactNumber,
    passportImageUrl,
  }: TPassportVerificationForm) => {
    const initialContactNumber = defaultValues?.contactNumber

    if (initialContactNumber !== contactNumber) {
      const isValidPhoneNumber = await validatePhoneNumberAsync(contactNumber)

      if (!isValidPhoneNumber) {
        return
      }
    }

    if (passportImageUrl) {
      onSuccess({ contactNumber, passportImageUrl })
    }
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Content className="h-[760px] gap-0 p-0">
        <Dialog.Header className="p-6">
          <Dialog.Title>{t('title')}</Dialog.Title>
          <Dialog.Description className="hidden">
            여권 사진을 등록해 주세요
          </Dialog.Description>
        </Dialog.Header>

        <div className="scrollbar thick flex grow flex-col overflow-auto p-6 pt-0">
          <p className="typo-label2 whitespace-break-spaces text-gray-500">
            {t('description')}
          </p>
          <Form.Root form={form} className="mt-10 space-y-10">
            <Form.Field name="contactNumber">
              <Form.Label className="typo-body1 mb-3 font-semibold">
                {t('phone_number_label')}
              </Form.Label>
              <Form.TextField
                type="tel"
                placeholder={t('phone_number_placeholder')}
                fullWidth
              />
              <Form.Message />
            </Form.Field>
            <PassportImageForm />
          </Form.Root>

          <ContentBox spacing="medium" color="light-gray" className="mt-3">
            <Notice.Title
              hasIcon={false}
              className="typo-label2 mb-2 font-medium text-purple-500"
            >
              {t('notice_title')}
            </Notice.Title>
            <Notice.List gap={false} className="text-black *:before:bg-black">
              <Notice.Item>{t('notice_item_passport_photo')}</Notice.Item>
              <Notice.Item>
                {t.rich('notice_item_passport_expiry', {
                  highlight: chunks => (
                    <strong className="font-bold">{chunks}</strong>
                  ),
                })}
              </Notice.Item>
              <Notice.Item className="font-bold">
                {t.rich('notice_item_hide_passport_number', {
                  highlight: chunks => (
                    <strong className="font-bold">{chunks}</strong>
                  ),
                })}
              </Notice.Item>
              <Notice.Item>{t('notice_item_take_front_photo')}</Notice.Item>
            </Notice.List>
            <h4 className="typo-label2 mt-4 font-medium text-purple-500">
              {t('example_image_title')}
            </h4>
            <SampleImage />
          </ContentBox>
        </div>

        <Dialog.Footer className="flex-row gap-2.5 p-6">
          <Button
            color="secondary"
            size="large"
            fullWidth
            onClick={onClose}
            className="flex-1"
          >
            {t('button.close')}
          </Button>
          <Button
            size="large"
            fullWidth
            className="flex-1"
            onClick={handleSubmit(submitForm)}
          >
            {t('button.complete_registration')}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  )
}

const PassportImageForm = memo(() => {
  const t = useTranslations('passport_verification')

  const { generateUploadUrl } = useUploadUrl()

  const {
    control,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext<TPassportVerificationForm>()

  const passportImageUrl = useWatch({
    control,
    name: 'passportImageUrl',
  })

  const handleUploadpassportImage = async ({ file }: { file: File }) => {
    const url = await generateUploadUrl(file, {
      fileName: file.name,
      fileSize: file.size,
      prefix: 'registid',
    })

    if (!url) return

    if (errors.passportImageUrl) {
      clearErrors('passportImageUrl')
    }

    setValue('passportImageUrl', url)
  }

  return (
    <Form.Field name="passportImageUrl">
      <Form.Label className="typo-body1 font-semibold">
        {t('passport_photo_label')}
      </Form.Label>
      {passportImageUrl && (
        <div className="flex justify-center rounded-sm border border-gray-100 bg-white py-3">
          <Image
            src={passportImageUrl}
            alt="여권"
            width={162}
            height={100}
            external={true}
            className="rounded-sm"
          />
        </div>
      )}
      <ImageUpload onChange={handleUploadpassportImage}>
        <Button fullWidth className="gap-2">
          {!passportImageUrl && <Icon name="plus" />}
          {passportImageUrl
            ? t('button.change_passport_photo')
            : t('button.upload_passport_photo')}
        </Button>
      </ImageUpload>
      <Form.Message />
    </Form.Field>
  )
})

PassportImageForm.displayName = 'PassportImageForm'

const SampleImage = memo(() => (
  <Image
    src={PassportSampleImage}
    alt="여권"
    width={304}
    height={198}
    className="mt-2 overflow-visible"
    external={false}
  />
))

SampleImage.displayName = 'SampleImage'
